import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Alert, CircularProgress,
  Grid, Card, CardContent, Tooltip, IconButton, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  Refresh as RefreshIcon,
  CheckCircle as PassIcon,
  Warning as WarnIcon,
  HelpOutline as NullIcon,
  TableChart as XlsxIcon,
  UploadFile as UploadIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { adminAPI } from '../../services/api';

/* ── helpers ── */
const n   = (v) => parseInt(v) || 0;
const pct = (val, total) => total ? Math.round((val / total) * 100) : 0;

const groupByModule = (rows) => {
  const map = new Map();
  rows.forEach(row => {
    if (!map.has(row.module_code)) map.set(row.module_code, []);
    map.get(row.module_code).push(row);
  });
  return Array.from(map.values());
};

const moduleTotal = (groups) => ({
  total:   groups.reduce((s, g) => s + n(g.total_students), 0),
  passed:  groups.reduce((s, g) => s + n(g.count_passed),   0),
  below10: groups.reduce((s, g) => s + n(g.count_below_10), 0),
  nullC:   groups.reduce((s, g) => s + n(g.count_null),     0),
});

/* ── XLSX export — flat rows, one per module+group ── */
const exportXLSX = (session, modules) => {
  const header = [
    'Code Module', 'Nom Module', 'Groupe',
    'Nombre Étudiants', 'Admis (≥ 10)', '% Admis',
    'Rattrapage (< 10)', '% Rattrapage', 'Absent', '% Absent',
  ];

  const dataRows = modules.map(g => {
    const total   = n(g.total_students);
    const passed  = n(g.count_passed);
    const below10 = n(g.count_below_10);
    const absent  = n(g.count_null);
    return [
      g.module_code,
      g.module_name_arb || g.module_name || g.module_code,
      g.group_name  || '—',
      total,
      passed,  total  ? `${pct(passed,  total)}%` : '—',
      below10, total  ? `${pct(below10, total)}%` : '—',
      absent,  total  ? `${pct(absent,  total)}%` : '—',
    ];
  });

  const ws = XLSX.utils.aoa_to_sheet([header, ...dataRows]);
  ws['!cols'] = [
    { wch: 14 }, { wch: 42 }, { wch: 10 },
    { wch: 16 }, { wch: 12 }, { wch: 10 },
    { wch: 16 }, { wch: 12 }, { wch: 10 }, { wch: 10 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Statistiques');
  XLSX.writeFile(wb, `stats-examens-${session.academic_year}.xlsx`);
};

/* ── Bar ── */
const DistBar = ({ passed, below10, nullC, total }) => {
  const p1 = pct(passed,  total);
  const p2 = pct(below10, total);
  const p3 = pct(nullC,   total);
  return (
    <Tooltip title={`${p1}% admis · ${p2}% échec · ${p3}% absent`} arrow>
      <Box sx={{ minWidth: 120 }}>
        <Box sx={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', bgcolor: '#f1f5f9' }}>
          <Box sx={{ width: `${p1}%`, bgcolor: '#16a34a' }} />
          <Box sx={{ width: `${p2}%`, bgcolor: '#dc2626' }} />
          <Box sx={{ width: `${p3}%`, bgcolor: '#9ca3af' }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: 0.4 }}>
          <Typography variant="caption" sx={{ color: '#16a34a', fontWeight: 700, fontSize: '0.65rem' }}>{p1}%</Typography>
          {p2 > 0 && <Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 700, fontSize: '0.65rem' }}>{p2}%</Typography>}
          {p3 > 0 && <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700, fontSize: '0.65rem' }}>{p3}%</Typography>}
        </Box>
      </Box>
    </Tooltip>
  );
};

/* ── StatCard ── */
const StatCard = ({ label, value, color, icon }) => (
  <Card elevation={0} sx={{ borderRadius: 3, border: `1.5px solid ${color}33`, height: '100%' }}>
    <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ width: 44, height: 44, borderRadius: 2, flexShrink: 0, bgcolor: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {React.cloneElement(icon, { sx: { fontSize: 22, color } })}
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={800} sx={{ color, lineHeight: 1 }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
      </Box>
    </CardContent>
  </Card>
);

/* ══════════════════════════════════════════
   Main component
══════════════════════════════════════════ */
const ExamStats = () => {
  const [data, setData]           = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError]         = useState(null);

  const [importOpen, setImportOpen]       = useState(false);
  const [importFile, setImportFile]       = useState(null);
  const [importBusy, setImportBusy]       = useState(false);
  const [importResult, setImportResult]   = useState(null);
  const [importError, setImportError]     = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await adminAPI.getExamStats();
      setData(res);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur de chargement');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openImportDialog = () => {
    setImportFile(null);
    setImportResult(null);
    setImportError(null);
    setImportOpen(true);
  };

  const handleImportSubmit = async () => {
    if (!importFile) return;
    setImportBusy(true);
    setImportError(null);
    setImportResult(null);
    try {
      const res = await adminAPI.importExamGrades(importFile);
      setImportResult(res);
      await load();
    } catch (err) {
      setImportError(err.response?.data?.error || "Erreur lors de l'import");
    } finally {
      setImportBusy(false);
    }
  };

  const modules = data?.modules || [];
  const session = data?.session;
  const grouped = useMemo(() => groupByModule(modules), [modules]);

  const totalStudents  = grouped.reduce((s, gs) => s + moduleTotal(gs).total,   0);
  const totalPassed    = grouped.reduce((s, gs) => s + moduleTotal(gs).passed,  0);
  const totalBelow10   = grouped.reduce((s, gs) => s + moduleTotal(gs).below10, 0);
  const totalNull      = grouped.reduce((s, gs) => s + moduleTotal(gs).nullC,   0);
  const totalRattrapage = totalBelow10 + totalNull;

  // For the percentage bar: only count modules where grades have been entered
  const gradedModules   = modules.filter(g => n(g.count_passed) > 0 || n(g.count_below_10) > 0);
  const gradedTotal     = gradedModules.reduce((s, g) => s + n(g.total_students), 0);
  const gradedPassed    = gradedModules.reduce((s, g) => s + n(g.count_passed),   0);
  const gradedBelow10   = gradedModules.reduce((s, g) => s + n(g.count_below_10), 0);
  const gradedNull      = gradedModules.reduce((s, g) => s + n(g.count_null),     0);
  const gradedRattrapage = gradedBelow10 + gradedNull;

  const pctPassed     = pct(gradedPassed,     gradedTotal);
  const pctBelow10    = pct(gradedBelow10,    gradedTotal);
  const pctNull       = pct(gradedNull,       gradedTotal);
  const pctRattrapage = pct(gradedRattrapage, gradedTotal);

  if (isLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>

      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1a2a3a 0%, #2c3e50 60%, #3d566e 100%)',
        borderRadius: 4, p: { xs: 2.5, md: 3.5 }, mb: 3, color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BarChartIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} lineHeight={1.2}>Statistiques des Résultats</Typography>
            <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.3 }}>
              {session ? `${session.name} — ${session.academic_year}` : 'Session active'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {session && (
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={openImportDialog}
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', borderRadius: 2, '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              Importer Notes
            </Button>
          )}
          {session && modules.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<XlsxIcon />}
              onClick={() => exportXLSX(session, modules)}
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', borderRadius: 2, '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              Exporter Excel
            </Button>
          )}
          <IconButton onClick={load} sx={{ color: 'white', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 2 }}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
      {!session && !error && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Aucune session d'examen active. Activez une session dans la planification des examens.
        </Alert>
      )}

      {session && (
        <>
          {/* Summary cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <StatCard label="Étudiants assignés" value={totalStudents} color="#1565c0" icon={<BarChartIcon />} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard label="Note ≥ 10" value={totalPassed} color="#15803d" icon={<PassIcon />} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard label="Note < 10" value={totalBelow10} color="#dc2626" icon={<WarnIcon />} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard label="Note absente" value={totalNull} color="#6b7280" icon={<NullIcon />} />
            </Grid>
          </Grid>

          {/* Rattrapage overview */}
          {totalStudents > 0 && (
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #e8ecef', p: 3, mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ mb: 2 }}>
                Aperçu pour la session de Rattrapage
              </Typography>

              {/* Big percentage bar */}
              <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', height: 28, borderRadius: 2, overflow: 'hidden', mb: 1 }}>
                  <Tooltip title={`Admis : ${totalPassed} étudiants (${pctPassed}%)`} arrow>
                    <Box sx={{ width: `${pctPassed}%`, bgcolor: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'width 0.4s' }}>
                      {pctPassed >= 8 && <Typography variant="caption" sx={{ color: 'white', fontWeight: 800, fontSize: '0.72rem' }}>{pctPassed}%</Typography>}
                    </Box>
                  </Tooltip>
                  <Tooltip title={`Rattrapage : ${totalBelow10} étudiants (${pctBelow10}%)`} arrow>
                    <Box sx={{ width: `${pctBelow10}%`, bgcolor: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'width 0.4s' }}>
                      {pctBelow10 >= 8 && <Typography variant="caption" sx={{ color: 'white', fontWeight: 800, fontSize: '0.72rem' }}>{pctBelow10}%</Typography>}
                    </Box>
                  </Tooltip>
                  <Tooltip title={`Absent : ${totalNull} étudiants (${pctNull}%)`} arrow>
                    <Box sx={{ width: `${pctNull}%`, bgcolor: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'width 0.4s' }}>
                      {pctNull >= 8 && <Typography variant="caption" sx={{ color: 'white', fontWeight: 800, fontSize: '0.72rem' }}>{pctNull}%</Typography>}
                    </Box>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: '#16a34a' }} />
                    <Typography variant="caption" color="text.secondary">Admis &nbsp;<strong>{pctPassed}%</strong></Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: '#f59e0b' }} />
                    <Typography variant="caption" color="text.secondary">Rattrapage (note &lt;10) &nbsp;<strong>{pctBelow10}%</strong></Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: '#94a3b8' }} />
                    <Typography variant="caption" color="text.secondary">Absent &nbsp;<strong>{pctNull}%</strong></Typography>
                  </Box>
                </Box>
              </Box>

              {/* Key rattrapage metrics */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 160, bgcolor: '#fefce8', border: '1.5px solid #fde68a', borderRadius: 2, p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={800} color="#b45309">{pctRattrapage}%</Typography>
                  <Typography variant="caption" color="#92400e" fontWeight={600}>concernés par le rattrapage</Typography>
                  <Typography variant="body2" color="#b45309" sx={{ mt: 0.5 }}>{gradedRattrapage} étudiants</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 160, bgcolor: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 2, p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={800} color="#15803d">{pctPassed}%</Typography>
                  <Typography variant="caption" color="#166534" fontWeight={600}>taux de validation</Typography>
                  <Typography variant="body2" color="#15803d" sx={{ mt: 0.5 }}>{gradedPassed} étudiants admis</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 160, bgcolor: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 2, p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={800} color="#475569">{pctNull}%</Typography>
                  <Typography variant="caption" color="#64748b" fontWeight={600}>absents (sans note)</Typography>
                  <Typography variant="body2" color="#475569" sx={{ mt: 0.5 }}>{gradedNull} étudiants</Typography>
                </Box>
              </Box>
              {gradedTotal < totalStudents && (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5, textAlign: 'right' }}>
                  * Basé sur {gradedTotal} étudiants dont les notes sont saisies — {totalStudents - gradedTotal} dans des modules sans notes encore.
                </Typography>
              )}
            </Paper>
          )}

          {/* Table */}
          {grouped.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>Aucun module dans la session active.</Alert>
          ) : (
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #e8ecef', overflow: 'hidden' }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Module</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Code</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Groupe</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#475569' }}>Total</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#15803d' }}>≥ 10</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#dc2626' }}>{'< 10'}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#6b7280' }}>Absent</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569', minWidth: 130 }}>Répartition</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {grouped.map((groups) => {
                      const first      = groups[0];
                      const tot        = moduleTotal(groups);
                      const multiGroup = groups.length > 1;

                      return (
                        <React.Fragment key={first.module_code}>
                          {/* Module header row */}
                          <TableRow sx={{ bgcolor: '#1e3a5f' }}>
                            <TableCell sx={{ color: 'white', fontWeight: 700, py: 1.2 }}>
                              {first.module_name_arb || first.module_name || first.module_code}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: 'rgba(255,255,255,0.15)', color: 'white', px: 1, py: 0.3, borderRadius: 1, fontWeight: 700 }}>
                                {first.module_code}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem' }}>
                              {multiGroup ? `${groups.length} groupes` : (first.group_name || '—')}
                            </TableCell>
                            <TableCell align="center"><Chip label={tot.total}   size="small" sx={{ fontWeight: 800, bgcolor: 'rgba(255,255,255,0.18)', color: 'white', height: 22, borderRadius: 1.5 }} /></TableCell>
                            <TableCell align="center"><Chip label={tot.passed}  size="small" sx={{ fontWeight: 800, bgcolor: '#dcfce7', color: '#15803d', height: 22, borderRadius: 1.5 }} /></TableCell>
                            <TableCell align="center"><Chip label={tot.below10} size="small" sx={{ fontWeight: 800, bgcolor: tot.below10 > 0 ? '#fee2e2' : 'rgba(255,255,255,0.1)', color: tot.below10 > 0 ? '#dc2626' : 'rgba(255,255,255,0.4)', height: 22, borderRadius: 1.5 }} /></TableCell>
                            <TableCell align="center"><Chip label={tot.nullC}   size="small" sx={{ fontWeight: 800, bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', height: 22, borderRadius: 1.5 }} /></TableCell>
                            <TableCell><DistBar passed={tot.passed} below10={tot.below10} nullC={tot.nullC} total={tot.total} /></TableCell>
                          </TableRow>

                          {/* Group sub-rows */}
                          {multiGroup && groups.map((g) => {
                            const gt = n(g.total_students);
                            return (
                              <TableRow key={`${g.module_code}-${g.group_name}`} sx={{ bgcolor: '#f8fafc', '&:hover': { bgcolor: '#f1f5f9' } }}>
                                <TableCell />
                                <TableCell />
                                <TableCell>
                                  <Chip label={g.group_name || '—'} size="small" sx={{ height: 20, fontSize: '0.72rem', fontWeight: 700, bgcolor: '#e0f2fe', color: '#0369a1', borderRadius: 1 }} />
                                </TableCell>
                                <TableCell align="center"><Typography variant="body2" fontWeight={600} color="#1565c0">{gt}</Typography></TableCell>
                                <TableCell align="center"><Typography variant="body2" fontWeight={700} color="#15803d">{n(g.count_passed)}</Typography></TableCell>
                                <TableCell align="center"><Typography variant="body2" fontWeight={700} color={n(g.count_below_10) > 0 ? '#dc2626' : '#94a3b8'}>{n(g.count_below_10)}</Typography></TableCell>
                                <TableCell align="center"><Typography variant="body2" fontWeight={600} color="#6b7280">{n(g.count_null)}</Typography></TableCell>
                                <TableCell><DistBar passed={n(g.count_passed)} below10={n(g.count_below_10)} nullC={n(g.count_null)} total={gt} /></TableCell>
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {/* Import grades dialog */}
      <Dialog open={importOpen} onClose={() => !importBusy && setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Importer des notes manuellement</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pour les modules dont les notes sont absentes de la base Oracle. Le fichier Excel doit
            contenir 3 colonnes : <strong>Apogée</strong>, <strong>Note</strong> (ou "ABS" pour absent)
            et <strong>Module</strong> (code).
          </Typography>

          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={(e) => {
              setImportFile(e.target.files?.[0] || null);
              setImportResult(null);
              setImportError(null);
            }}
          />
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={importBusy}
            sx={{ borderRadius: 2 }}
          >
            {importFile ? importFile.name : 'Choisir un fichier Excel'}
          </Button>

          {importError && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{importError}</Alert>}

          {importResult && (
            <Box sx={{ mt: 2 }}>
              <Alert severity={importResult.errors?.length ? 'warning' : 'success'} sx={{ borderRadius: 2 }}>
                {importResult.imported} / {importResult.total} notes importées avec succès.
              </Alert>
              {importResult.errors?.length > 0 && (
                <List dense sx={{ maxHeight: 200, overflow: 'auto', mt: 1, bgcolor: '#fff7ed', borderRadius: 2 }}>
                  {importResult.errors.map((e, i) => (
                    <ListItem key={i}><ListItemText primary={e} primaryTypographyProps={{ variant: 'caption' }} /></ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)} disabled={importBusy}>Fermer</Button>
          <Button
            variant="contained"
            onClick={handleImportSubmit}
            disabled={!importFile || importBusy}
            startIcon={importBusy ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
          >
            {importBusy ? 'Import en cours...' : 'Importer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExamStats;
